import {OutlinedCard} from '../outlined/OutlinedCard';
import React, {useState} from 'react';
import {Eyebrow} from '../../eyebrow';
import {VerticalSpacer} from '../../spacer';
import {Typography} from '../../typography';
import {Select, SelectMenuItem, TextField} from '../../inputs';
import {ArrowRight, CheckIcon} from '../../icons';
import {NewsletterCardWrapper} from './NewsletterCard.styles';
import {Hyperlink} from '../../links';
import IconButton from '@material-ui/core/IconButton';
import {NewsletterProps} from '../../../../types';
import {getLocale} from '@epic-core/common';
import {withNewsletterLogic} from '../../../wraps/newsletter/Newsletter.wrap';

export const NewsletterCard = withNewsletterLogic(
    ({
        accentColor = 'sky',
        eyebrow,
        title,
        emailInputValue,
        emailInputError,
        emailInputHelperText,
        emailInputDisable,
        emailInputPlaceholder,
        industryInputError,
        industryInputHelperText,
        industryPlaceholder,
        privacyPolicyHref,
        industrySelected,
        privacyPolicyDescription,
        privacyPolicyLinkName = 'Privacy policy',
        industries = [],
        onChange,
        onSubmit,
        onBlur,
        disableSubmitButton,
        disable,
        hideSubmitButton,
        subscribed,
        successTitle,
        successMessage
    }: NewsletterProps): JSX.Element => {
        const selectMenuItems: JSX.Element[] = [];
        const validPlaceholder =
            industryPlaceholder &&
            Boolean(industries.filter(({name}) => name === industryPlaceholder).length);
        const [selectedIndustry, setSelectedIndustry] = useState(
            validPlaceholder ? industryPlaceholder : industrySelected
        );
        const [email, setEmail] = useState<string | undefined>(emailInputValue);

        let placeholder = industryPlaceholder || 'your industry';

        const getLocalizedIndustryName = (id: string) => {
            for (const industry of industries) {
                if (industry.id === id) {
                    return industry.name;
                }
            }
            return id;
        };

        const getIndustryId = (name: string) => {
            for (const industry of industries) {
                if (industry.name === name) {
                    return industry.id;
                }
            }
            return name;
        };

        for (const industry of industries) {
            if (industry.id === 'placeholder') {
                placeholder = industry.name || placeholder;
            } else {
                selectMenuItems.push(
                    <SelectMenuItem id={industry.id} key={industry.id} value={industry.id}>
                        {industry.name}
                    </SelectMenuItem>
                );
            }
        }

        const handleChange = (event: React.ChangeEvent<{name?: string; value: unknown}>) => {
            switch (event.target.name) {
                case 'email':
                    if (onChange && typeof onChange === 'function')
                        onChange('email', event.target.value as string);
                    setEmail(event.target.value as string);
                    break;
                case 'industry':
                    if (onChange && typeof onChange === 'function')
                        onChange('industry', event.target.value as string);
                    setSelectedIndustry(getLocalizedIndustryName(event.target.value as string));
                    break;
                default:
                    break;
            }
        };

        const handleBlur = (event: React.ChangeEvent<{name?: string; value: unknown}>) => {
            switch (event.target.name) {
                case 'email':
                    if (onBlur && typeof onBlur === 'function')
                        onBlur('email', event.target.value as string);
                    break;
                case 'industry':
                    if (onBlur && typeof onBlur === 'function')
                        onBlur('industry', event.target.value as string);
                    break;
                default:
                    break;
            }
        };

        return (
            <NewsletterCardWrapper
                accentColor={accentColor}
                paddingBottom={emailInputHelperText ? '18px' : '0px'}>
                <OutlinedCard accentColor={accentColor} topSpacing={false}>
                    <Eyebrow {...eyebrow} />
                    {subscribed && (
                        <>
                            <VerticalSpacer variant="custom" spacing={8} />
                            <CheckIcon color={accentColor} />
                        </>
                    )}
                    <VerticalSpacer variant="eyebrow" />
                    <Typography variant="h3" big>
                        {subscribed ? successTitle : title}
                    </Typography>
                    {!subscribed && (
                        <>
                            <Typography variant="h3" big>
                                <Select
                                    onChange={handleChange}
                                    name="industry"
                                    accentColor={accentColor}
                                    value={selectedIndustry}
                                    error={industryInputError}
                                    helperText={industryInputHelperText}
                                    disabled={disable}
                                    displayEmpty={true}
                                    renderValue={(value: any) => (value ? value : placeholder)}>
                                    {selectMenuItems}
                                </Select>
                            </Typography>
                            <VerticalSpacer variant="custom" spacing={4} />
                            <TextField
                                onChange={handleChange}
                                name="email"
                                onBlur={handleBlur}
                                value={emailInputValue ? emailInputValue : email}
                                error={emailInputError}
                                helperText={emailInputHelperText}
                                placeholder={emailInputPlaceholder}
                                disabled={emailInputDisable || disable}
                                endAdornment={
                                    !hideSubmitButton && (
                                        <IconButton
                                            aria-label="Subscribe"
                                            disabled={disable || disableSubmitButton}
                                            classes={{
                                                root: 'icon-button-root'
                                            }}
                                            onClick={() => {
                                                if (onSubmit && typeof onSubmit === 'function') {
                                                    onSubmit(
                                                        email,
                                                        selectedIndustry &&
                                                            getIndustryId(selectedIndustry)
                                                    );
                                                }
                                            }}>
                                            <ArrowRight />
                                        </IconButton>
                                    )
                                }
                            />
                        </>
                    )}
                    <VerticalSpacer variant="custom" spacing={2} />
                    <Typography variant="p" caption>
                        {subscribed ? (
                            <>
                                {successMessage}
                                <VerticalSpacer variant="custom" spacing={8} />
                            </>
                        ) : (
                            <>
                                {privacyPolicyDescription}
                                <br />
                                <Hyperlink
                                    target="_blank"
                                    accentColor={accentColor}
                                    href={
                                        privacyPolicyHref ||
                                        `https://www.epicgames.com/${getLocale()}/privacypolicy`
                                    }>
                                    {privacyPolicyLinkName}
                                </Hyperlink>
                            </>
                        )}
                    </Typography>
                </OutlinedCard>
            </NewsletterCardWrapper>
        );
    }
);
